import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { faCircleInfo, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import { AlreadyConnectedCheck } from "./useAlreadyConnectedCheck";

const ts = (key: string) => `list.reachOut.alreadyConnected.${key}`;

/**
 * The confirmed PREC-4426 mockup's states, inline under the picked campaign: the amber warning for a
 * connection to the campaign's sender (one lead, or "N of M" with the leads and the skip box), the
 * grey hint for a lead connected only to a colleague. Never blocks the button.
 */
export const AlreadyConnectedNoticeView: FunctionComponent<{ check: AlreadyConnectedCheck }> = ({
	check,
}) => {
	const { t } = useTranslation();
	const [showLeads, setShowLeads] = useState(false);
	const { notice } = check;

	if (check.checkFailed) {
		return <p className="text-[11px] text-text-meta">{t(ts("checkFailed"))}</p>;
	}

	if (notice.kind === "none") {
		return null;
	}

	if (notice.kind === "colleague") {
		return (
			<div
				className="flex items-start gap-2 rounded-lg border border-border-light bg-status-info-light p-2 text-xs text-text-meta"
				data-testid="already-connected-hint"
			>
				<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faCircleInfo} />
				<span>
					{t(ts("colleagueHint"), {
						lead: notice.lead.name ?? t(ts("unnamedLead")),
						colleagues: notice.lead.connectedTo.join(", "),
					})}
				</span>
			</div>
		);
	}

	const bulk = notice.kind === "senderBulk";

	return (
		<>
			<div
				className="flex items-start gap-2 rounded-lg border border-tag-warning-border bg-background-warning p-2 text-xs text-tag-warning-dark"
				data-testid="already-connected-warning"
				role="status"
			>
				<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
				<div className="flex flex-col gap-1">
					<span className="font-semibold">
						{bulk
							? t(ts("bulkTitle"), { count: notice.count, total: notice.total })
							: t(ts("title"))}
					</span>
					<span>
						{bulk
							? t(ts(notice.stops ? "bulkStops" : "bulkSkips"))
							: t(ts(notice.stops ? "singleStops" : "singleSkips"), {
									lead: notice.lead.name ?? t(ts("unnamedLead")),
									senders: notice.lead.connectedTo.join(", "),
								})}
					</span>
					{bulk && (
						<button
							className="w-fit cursor-pointer text-[11px] font-semibold text-blue-action hover:underline"
							type="button"
							onClick={() => setShowLeads((open) => !open)}
						>
							{showLeads ? t(ts("hideLeads")) : t(ts("showLeads"), { count: notice.count })}
						</button>
					)}
					{bulk && showLeads && (
						<ul
							className="flex flex-col gap-0.5 text-text-meta"
							data-testid="already-connected-leads"
						>
							{notice.leads.map((lead) => (
								<li key={lead.listPersonId} className="flex justify-between gap-2">
									<span className="truncate">
										{[lead.name, lead.companyName].filter(Boolean).join(" · ")}
									</span>
									<span className="shrink-0">↔ {lead.connectedTo.join(", ")}</span>
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
			{bulk && (
				<Checkbox
					labelClass="text-xs font-semibold text-brand-black"
					isChecked={check.skipConnected}
					testId="already-connected-skip"
					onChange={() => check.setSkipConnected(!check.skipConnected)}
				>
					{t(ts("skip"), { count: notice.count })}
				</Checkbox>
			)}
		</>
	);
};
